import { Link } from "react-router-dom";

const FOUNDER_IMG = "https://customer-assets-m6fa6gv7.emergentagent.net/job_ba30-app/artifacts/gh8kkxfh_73b7c5b9-179d-4f61-a0c8-77fbbf477647.webp";

export default function About() {
  return (
    <div className="max-w-5xl mx-auto px-6 md:px-12 py-20">
      <div className="label-eyebrow">Founder Story</div>
      <h1 className="wordmark mt-4 text-5xl md:text-7xl uppercase leading-[0.9]">
        Why I <span className="text-primary">Built BA30.</span>
      </h1>

      <div className="mt-12 grid lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-5 lg:sticky lg:top-24">
          <div className="aspect-[4/5] rounded-lg overflow-hidden border border-border">
            <img src={FOUNDER_IMG} alt="Troy — BA30 founder" className="w-full h-full object-cover" data-testid="founder-image" />
          </div>
          <figcaption className="mt-4 text-center" data-testid="founder-caption">
            <div className="font-head uppercase text-lg text-foreground leading-tight">Troyson Bassani</div>
            <div className="mt-1 text-xs uppercase tracking-widest text-muted-foreground">Founder · BA30</div>
          </figcaption>
          <div className="mt-6 grid grid-cols-2 gap-3">
            <Stat n="106 → 83" l="kg" />
            <Stat n="~35% → 12-14%" l="body fat" />
            <Stat n="2" l="titanium plates" />
            <Stat n="17" l="screws" />
          </div>
        </div>

        <article className="lg:col-span-7 text-foreground/85 leading-relaxed text-lg space-y-6" data-testid="founder-story">
          <p>For most of my life, fitness meant training like an athlete.</p>

          <p>I played semi-professional Rugby League for the Central Queensland Capras. I trained hard, ran constantly, lifted weights and built my life around performance.</p>

          <p className="font-head uppercase text-2xl text-foreground">At 26, all of that changed.</p>

          <p>I suffered a catastrophic ankle injury, fracturing both my tibia and fibula. One of the surgeons described it to me like a bomb had gone off inside my ankle.</p>

          <p>I eventually walked away with <span className="text-primary font-semibold">two titanium plates and 17 screws</span> holding everything together.</p>

          <p>My Rugby League career was over.</p>

          <p>But what I didn't realise at the time was that I had also lost the only way I knew how to stay fit.</p>

          <p>Running became painful. If I pushed it too hard, my ankle would swell and ache. The training I had relied on for years was no longer available to me.</p>

          <p>So slowly, fitness disappeared from my life.</p>

          <div className="border-l-2 border-primary pl-6 py-2 my-8">
            <p className="text-foreground">A few years later, I had gone from being a lean 95&nbsp;kg rugby player at roughly 15% body fat to around <span className="font-semibold">106&nbsp;kg and approximately 35% body fat</span>.</p>
          </div>

          <p>I still wanted to feel athletic.</p>
          <p>I still wanted to look strong.</p>
          <p>But I couldn't train like the athlete I used to be.</p>

          <p>So I did what a lot of people do.</p>

          <ul className="space-y-1 text-foreground/85 pl-1">
            <li>I tried more.</li>
            <li>Online coaches.</li>
            <li>Personal trainers.</li>
            <li>Six-day gym programs.</li>
            <li>Extreme dieting.</li>
            <li>Fasting.</li>
            <li>Tracking everything.</li>
          </ul>

          <p>Trying to force myself back into the body I used to have.</p>
          <p className="font-head uppercase text-xl text-foreground">None of it lasted.</p>

          <p>Eventually I realised the problem wasn't that I wasn't doing enough.</p>

          <p className="font-head uppercase text-xl text-primary">I was still trying to train for the life I had at 25 instead of the life I had after 30.</p>

          <p>So I started simplifying everything.</p>

          <h2 className="font-display uppercase text-3xl md:text-4xl text-foreground mt-10 pt-6 border-t border-border">Instead of running, I walked.</h2>
          <p>Walking gave me a low-impact way to dramatically increase my daily movement without constantly aggravating my ankle or crushing my recovery.</p>
          <p>I built toward <span className="font-semibold text-foreground">roughly 10,000 steps a day</span>.</p>

          <h2 className="font-display uppercase text-3xl md:text-4xl text-foreground mt-10 pt-6 border-t border-border">Instead of six days a week, I trained three.</h2>
          <p>Three proper resistance-training sessions. Three sessions I could actually attack with intent.</p>
          <ul className="space-y-1 pl-1">
            <li>Get stronger.</li>
            <li>Maintain and build muscle.</li>
            <li>Then recover.</li>
          </ul>

          <h2 className="font-display uppercase text-3xl md:text-4xl text-foreground mt-10 pt-6 border-t border-border">Instead of extreme diets, I ate real food.</h2>
          <p>Instead of bouncing between restrictive diets, fasting protocols and obsessive tracking, I made food simpler too.</p>
          <ul className="space-y-1 pl-1">
            <li>Protein.</li>
            <li>Mostly whole foods.</li>
            <li>Reasonable portions.</li>
            <li>Enough flexibility that I could still live my life.</li>
          </ul>

          <p className="mt-10">And for the first time since my injury, everything started working together.</p>

          <div className="wordmark uppercase text-3xl md:text-4xl text-foreground leading-tight py-6">
            Walking.<br />Lifting.<br />Eating properly.<br />Recovering.<br /><span className="text-primary">Repeating.</span>
          </div>

          <div className="bg-foreground text-background rounded-lg p-6 md:p-8 my-8">
            <div className="label-eyebrow !text-background/70">The Result</div>
            <p className="mt-3 font-head uppercase text-2xl md:text-3xl leading-tight">
              I went from 106&nbsp;kg at ~35% body fat to <span className="text-primary">83&nbsp;kg and 12–14% body fat.</span>
            </p>
          </div>

          <p>But the biggest thing I learned wasn't how to lose weight.</p>
          <p className="font-head uppercase text-xl text-foreground">It was that I never needed to train like an athlete again.</p>

          <p>I needed a system designed for the person I had become.</p>

          <ul className="space-y-1 pl-1 text-foreground/85">
            <li>Someone over 30.</li>
            <li>Someone with injuries.</li>
            <li>Someone with work, family and responsibilities.</li>
            <li>Someone who still wanted to look good, feel strong, be lean and stay healthy — but didn't want fitness to consume his life.</li>
          </ul>

          <p className="pt-4">That became the foundation of Built After 30.</p>
          <p>And eventually, the 10K30 Method.</p>

          <div className="wordmark uppercase text-4xl md:text-5xl leading-tight py-6 border-y border-border my-8">
            Walk More.<br />Lift Heavy.<br />Eat Real Food.<br /><span className="text-primary">Live Your Life.</span>
          </div>

          <p>BA30 is for people over 30 who want to stay strong, healthy and capable for the years ahead — and still care about how they look and feel — without making fitness their entire life.</p>

          <p className="font-head uppercase text-2xl text-foreground">
            You don't need to train like an athlete.<br />
            <span className="text-primary">You need a system you can actually live with.</span>
          </p>

          <p className="font-head uppercase text-xl">That's why I built BA30.</p>

          <div className="pt-10 flex flex-wrap gap-3">
            <Link to="/join" className="btn-primary" data-testid="founder-cta-join">Start My Rebuild</Link>
            <Link to="/10k30" className="btn-secondary" data-testid="founder-cta-10k30">See the 10K30 Program</Link>
          </div>
        </article>
      </div>
    </div>
  );
}

function Stat({ n, l }) {
  return (
    <div className="bg-card border border-border rounded-md p-3">
      <div className="wordmark text-lg text-foreground leading-tight">{n}</div>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">{l}</div>
    </div>
  );
}
